import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import izenuAPI from '../../services/izenuAPI';

// Moderação → Músicas reportadas: tracks listeners reported from the web app
// ("Reportar…" in a song's menu), grouped by track. Resolving acts on every
// open report of the track at once (MusicContentReport::resolve).

const PER_PAGE = 30;

const STATE_LABEL = { approved: 'Aprovada', rejected: 'Recusada', unreviewed: 'Pendente' };

const fmtDate = (s) => (s ? new Date(s.replace(' ', 'T')).toLocaleString('pt-PT', { dateStyle: 'short', timeStyle: 'short' }) : '');

const STATUS_LABEL = { dismissed: 'Dispensada', hidden: 'Ocultada', ai: 'Marcada IA' };

export default function MusicReports() {
    const [status, setStatus] = useState('open');
    const [page, setPage] = useState(1);
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(null);
    const [aiFor, setAiFor] = useState(null);
    const [aiReason, setAiReason] = useState('');

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const { data } = await izenuAPI.get('/admin/music_reports/list.php', { params: { status, page, per_page: PER_PAGE } });
            if (!data.response) throw new Error();
            setRows(data.data);
            setTotal(data.total);
        } catch {
            setError('Não foi possível carregar as denúncias.');
        }
        setLoading(false);
    }, [status, page]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => setPage(1), [status]);

    const resolve = async (musicId, action, reason) => {
        if (action === 'hide' && !window.confirm('Ocultar esta música? Fica recusada e deixa de tocar para toda a gente.')) return;
        setBusy(musicId);
        try {
            const { data } = await izenuAPI.post('/admin/music_reports/resolve.php', { music_id: musicId, action, reason });
            if (!data.response) throw new Error(data.message);
            setRows((prev) => prev.filter((r) => r.music_id !== musicId));
            setTotal((t) => Math.max(0, t - 1));
            setAiFor(null);
            setAiReason('');
            window.dispatchEvent(new Event('musicreportschange'));
        } catch (e) {
            const msg = e?.response?.data?.message || e.message || 'Não foi possível guardar.';
            window.alert(msg);
        }
        setBusy(null);
    };

    const pages = Math.max(1, Math.ceil(total / PER_PAGE));
    const btn = 'text-xs font-semibold rounded-full px-3 py-1.5 disabled:opacity-50';

    return (
        <div className="p-6">
            <div style={{ marginBottom: 24 }}>
                <h2 style={{ fontSize: 24, fontWeight: 700, color: '#333', marginBottom: 8 }}>Músicas reportadas</h2>
                <p style={{ fontSize: 14, color: '#666', lineHeight: 1.6, marginBottom: 12 }}>
                    Denúncias feitas pelos ouvintes no web app. Cada decisão fecha todas as denúncias abertas da música.
                    <b> Ocultar</b> recusa a música (deixa de tocar para toda a gente); <b>Marcar IA</b> segue as regras das
                    músicas feitas com IA e avisa o artista.
                </p>
                <span style={{ fontSize: 13, color: status === 'open' && total > 0 ? '#E5004D' : '#6366f1', fontWeight: 600 }}>
                    {total.toLocaleString('pt-PT')} {total === 1 ? 'música' : 'músicas'}
                </span>
            </div>

            <div className="flex items-center gap-2 mb-4">
                {[['open', 'Por rever'], ['closed', 'Resolvidas']].map(([key, label]) => (
                    <button
                        key={key}
                        type="button"
                        onClick={() => setStatus(key)}
                        className={`text-sm rounded-full px-4 py-1.5 border ${status === key ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-700 border-gray-300'}`}
                    >
                        {label}
                    </button>
                ))}
            </div>

            {error && <p className="text-red-600 text-sm mb-4">{error}</p>}
            {loading && <p className="text-gray-500 text-sm">A carregar…</p>}
            {!loading && !error && rows.length === 0 && (
                <p className="text-gray-500 text-sm">{status === 'open' ? 'Nenhuma denúncia por rever.' : 'Ainda não há denúncias resolvidas.'}</p>
            )}

            <div className="flex flex-col gap-4">
                {rows.map((r) => (
                    <div key={r.music_id} className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
                        <div className="flex flex-wrap items-start gap-4">
                            {r.cover_url ? (
                                <img src={r.cover_url} alt="" className="w-16 h-16 rounded-lg object-cover flex-shrink-0" />
                            ) : (
                                <div className="w-16 h-16 rounded-lg bg-gray-200 flex-shrink-0" />
                            )}
                            <div className="flex-1 min-w-[200px]">
                                <div className="flex flex-wrap items-center gap-2">
                                    <span className="font-semibold text-gray-900">{r.title}</span>
                                    <span className="text-xs text-gray-500">#{r.music_id}</span>
                                    <span className="text-xs font-semibold rounded px-2 py-0.5" style={{ background: '#eef2ff', color: '#4338ca' }}>
                                        {STATE_LABEL[r.music_state] || r.music_state}
                                    </span>
                                    {Number(r.ai_generated) === 1 && (
                                        <span className="text-xs font-semibold rounded px-2 py-0.5" style={{ background: '#f3e8ff', color: '#7e22ce' }}>IA</span>
                                    )}
                                    <span className="text-xs font-semibold rounded-full px-2 py-0.5" style={{ background: '#fee2e2', color: '#b91c1c' }}>
                                        {r.reports} {r.reports === 1 ? 'denúncia' : 'denúncias'}
                                    </span>
                                </div>
                                <Link to={`/list-musics?user_id=${r.user_id}`} className="text-sm text-indigo-600 hover:underline">
                                    {r.artist_name || r.singer}
                                </Link>
                                {r.audio_url && <audio controls preload="none" src={r.audio_url} className="mt-2 w-full max-w-md h-9" />}
                            </div>
                            {status === 'open' && (
                                <div className="flex flex-wrap gap-2">
                                    <button type="button" disabled={busy === r.music_id} onClick={() => resolve(r.music_id, 'dismiss')} className={btn} style={{ background: '#e5e7eb', color: '#374151' }}>
                                        Dispensar
                                    </button>
                                    <button type="button" disabled={busy === r.music_id} onClick={() => { setAiFor(r.music_id); setAiReason(''); }} className={btn} style={{ background: '#7e22ce', color: '#fff' }}>
                                        Marcar IA
                                    </button>
                                    <button type="button" disabled={busy === r.music_id} onClick={() => resolve(r.music_id, 'hide')} className={btn} style={{ background: '#dc2626', color: '#fff' }}>
                                        Ocultar música
                                    </button>
                                </div>
                            )}
                        </div>

                        {aiFor === r.music_id && (
                            <div className="mt-3 flex flex-wrap items-center gap-2">
                                <input
                                    value={aiReason}
                                    onChange={(e) => setAiReason(e.target.value)}
                                    maxLength={255}
                                    placeholder="Motivo (vai no email ao artista)"
                                    className="text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white flex-1 min-w-[240px]"
                                />
                                <button type="button" disabled={!aiReason.trim() || busy === r.music_id} onClick={() => resolve(r.music_id, 'ai', aiReason.trim())} className={btn} style={{ background: '#7e22ce', color: '#fff' }}>
                                    Confirmar
                                </button>
                                <button type="button" onClick={() => setAiFor(null)} className={btn} style={{ background: '#e5e7eb', color: '#374151' }}>
                                    Cancelar
                                </button>
                            </div>
                        )}

                        <ul className="mt-3 divide-y divide-gray-100 border-t border-gray-100">
                            {r.entries.map((e) => (
                                <li key={e.id} className="py-2 text-sm">
                                    <div className="flex flex-wrap items-center gap-2">
                                        <span className="font-semibold text-gray-800">{e.reason_label}</span>
                                        <span className="text-gray-400">·</span>
                                        <span className="text-gray-600">{e.reporter_name || `#${e.user_id}`}</span>
                                        {e.reporter_email && <span className="text-gray-400 text-xs">{e.reporter_email}</span>}
                                        <span className="text-gray-400 text-xs ms-auto">{fmtDate(e.created_at)}</span>
                                        {e.status !== 'open' && (
                                            <span className="text-xs text-gray-500">{STATUS_LABEL[e.status]} {fmtDate(e.reviewed_at)}</span>
                                        )}
                                    </div>
                                    {e.note && <p className="text-gray-700 mt-1 whitespace-pre-wrap">{e.note}</p>}
                                </li>
                            ))}
                        </ul>
                    </div>
                ))}
            </div>

            {pages > 1 && (
                <div className="flex items-center gap-3 mt-6 text-sm">
                    <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="px-3 py-1.5 border rounded-lg disabled:opacity-40">Anterior</button>
                    <span>Página {page} de {pages}</span>
                    <button type="button" disabled={page >= pages} onClick={() => setPage((p) => p + 1)} className="px-3 py-1.5 border rounded-lg disabled:opacity-40">Seguinte</button>
                </div>
            )}
        </div>
    );
}
